import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  client,
  OperationalHoursResponseDto,
  OperationalHoursEntryResponseDto,
} from "@/client";

const MAX_DETAIL_FETCHES = 50;

function areSchedulesEqual(
  a: OperationalHoursEntryResponseDto[],
  b: OperationalHoursEntryResponseDto[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every((entryA) => {
    const entryB = b.find((e) => e.dayOfWeek === entryA.dayOfWeek);
    if (!entryB) return false;
    return (
      entryA.startTime === entryB.startTime &&
      entryA.endTime === entryB.endTime &&
      entryA.enabled === entryB.enabled
    );
  });
}

function areOperationalHoursEqual(
  a: OperationalHoursResponseDto,
  b: OperationalHoursResponseDto,
): boolean {
  return (
    a.enabled === b.enabled &&
    a.timezone === b.timezone &&
    areSchedulesEqual(a.schedule, b.schedule)
  );
}

/**
 * Finds a common OperationalHoursResponseDto among non-null items.
 * Returns the common value if all non-null items match, otherwise null.
 */
function resolveCommonOperationalHours(
  items: (OperationalHoursResponseDto | null | undefined)[],
): OperationalHoursResponseDto | null {
  const nonNull = items.filter(
    (item): item is OperationalHoursResponseDto => item != null,
  );
  if (nonNull.length === 0) return null;
  const first = nonNull[0];
  const allMatch = nonNull.every((item) =>
    areOperationalHoursEqual(first, item),
  );
  return allMatch ? first : null;
}

interface UseOperationalHoursDefaultsOptions {
  selectedIds: number[];
  isAllSelected: boolean;
  mode: "sites" | "siteGroups";
}

interface UseOperationalHoursDefaultsResult {
  defaultValue: OperationalHoursResponseDto | null | undefined;
  isLoading: boolean;
}

export function useOperationalHoursDefaults({
  selectedIds,
  isAllSelected,
  mode,
}: UseOperationalHoursDefaultsOptions): UseOperationalHoursDefaultsResult {
  const shouldFetchDetails =
    !isAllSelected &&
    selectedIds.length > 0 &&
    selectedIds.length <= MAX_DETAIL_FETCHES;

  // Account default (from cached user details)
  const userQuery = useQuery({
    ...client.users.me.asQueryOptions(),
    staleTime: 5 * 60 * 1000,
  });
  const accountDefault = userQuery.data?.defaultOperationalHours ?? null;

  // Fetch details for each selected item
  const siteDetailQueries = useQueries({
    queries:
      shouldFetchDetails && mode === "sites"
        ? selectedIds.map((id) => ({
            ...client.sites.getDetails.assign({ id }).asQueryOptions({}),
            staleTime: 60 * 1000,
          }))
        : [],
  });
  const siteGroupDetailQueries = useQueries({
    queries:
      shouldFetchDetails && mode === "siteGroups"
        ? selectedIds.map((id) => ({
            ...client.siteGroups.details.assign({ id }).asQueryOptions({}),
            staleTime: 60 * 1000,
          }))
        : [],
  });
  const detailQueries =
    mode === "sites" ? siteDetailQueries : siteGroupDetailQueries;

  const detailsLoading =
    shouldFetchDetails && detailQueries.some((q) => q.isLoading);
  const detailsReady =
    shouldFetchDetails && detailQueries.every((q) => q.isSuccess);

  // For sites: extract unique group ids when direct resolution fails.
  // API payloads use `groupId`; keep `siteGroupId` as a compatibility fallback.
  const siteGroupIds = useMemo(() => {
    if (mode !== "sites" || !detailsReady) return [];
    const ids = new Set<number>();
    for (const q of detailQueries) {
      const data = q.data as
        | { groupId?: number | null; siteGroupId?: number | null }
        | undefined;
      const groupId = data?.groupId ?? data?.siteGroupId;
      if (typeof groupId === "number") ids.add(groupId);
    }
    return Array.from(ids);
  }, [mode, detailsReady, detailQueries]);

  // For sites: try group fallback by fetching group details
  const shouldFetchGroups =
    mode === "sites" &&
    siteGroupIds.length > 0 &&
    siteGroupIds.length <= MAX_DETAIL_FETCHES;
  const groupQueries = useQueries({
    queries: shouldFetchGroups
      ? siteGroupIds.map((id) => ({
          ...client.siteGroups.details.assign({ id }).asQueryOptions({}),
          staleTime: 60 * 1000,
        }))
      : [],
  });

  const groupsLoading =
    shouldFetchGroups && groupQueries.some((q) => q.isLoading);
  const groupsReady =
    shouldFetchGroups && groupQueries.every((q) => q.isSuccess);

  return useMemo((): UseOperationalHoursDefaultsResult => {
    // "Select All" or too many items → account default
    if (isAllSelected || selectedIds.length > MAX_DETAIL_FETCHES) {
      return { defaultValue: accountDefault, isLoading: userQuery.isLoading };
    }

    // No items selected
    if (selectedIds.length === 0) {
      return { defaultValue: null, isLoading: false };
    }

    // Still loading detail data
    if (detailsLoading) {
      return { defaultValue: undefined, isLoading: true };
    }

    // Details ready → try common value
    if (detailsReady) {
      const opHoursList = detailQueries.map(
        (q) =>
          (q.data as { operationalHours?: OperationalHoursResponseDto })
            ?.operationalHours ?? null,
      );

      // Case 1: All same → use that value
      const common = resolveCommonOperationalHours(opHoursList);
      if (common) return { defaultValue: common, isLoading: false };

      // Case 2: Some have values, rest null → check if the non-null ones agree
      const nonNull = opHoursList.filter((v) => v != null);
      const hasNulls = opHoursList.some((v) => v == null);
      if (hasNulls && nonNull.length > 0) {
        const partialCommon = resolveCommonOperationalHours(nonNull);
        if (partialCommon)
          return { defaultValue: partialCommon, isLoading: false };
      }

      // Case 3 (sites only): Try site group fallback
      if (mode === "sites") {
        if (groupsLoading) {
          return { defaultValue: undefined, isLoading: true };
        }
        if (groupsReady && groupQueries.length > 0) {
          // Single group → use its op hours
          if (siteGroupIds.length === 1) {
            const groupOpHours = (
              groupQueries[0].data as {
                operationalHours?: OperationalHoursResponseDto;
              }
            )?.operationalHours;
            if (groupOpHours)
              return { defaultValue: groupOpHours, isLoading: false };
          }
          // Multiple groups → check if all groups have same op hours
          const groupOpHoursList = groupQueries.map(
            (q) =>
              (q.data as { operationalHours?: OperationalHoursResponseDto })
                ?.operationalHours ?? null,
          );
          const commonGroup = resolveCommonOperationalHours(groupOpHoursList);
          if (commonGroup)
            return { defaultValue: commonGroup, isLoading: false };
        }
      }

      // Final fallback → account default
      return { defaultValue: accountDefault, isLoading: userQuery.isLoading };
    }

    // Queries failed or not started
    return { defaultValue: accountDefault, isLoading: userQuery.isLoading };
  }, [
    isAllSelected,
    selectedIds.length,
    detailsLoading,
    detailsReady,
    detailQueries,
    groupsLoading,
    groupsReady,
    groupQueries,
    siteGroupIds,
    mode,
    accountDefault,
    userQuery.isLoading,
  ]);
}
